import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useManualQuery } from 'graphql-hooks';
import { Box, IconButton } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVolumeHigh, faVolumeMute } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import SwipeableViews from 'react-swipeable-views';

import { GUEST_HOME_VIDEOS } from '../../graphql/video/query';
import { Context, useAuth } from '../../context/auth';
import { User, Video } from '../../types';
import { MobileFeedItem } from '../../components/MobileFeedItem';
import ShortModal from '../../components/Modals/ShortModal/ShortModal';
import styles from './style';
import { EXP, installDebug, log } from './debug';

const PAGE_LIMIT = 5;
// The next page is asked when 2 videos or less are left after the current one.
const PRELOAD_THRESHOLD = 2;

interface MobileFeedProps {
  isMobile: boolean;
  innerHeight: number;
  user: User;
  // Shared link: its video first.
  firstVideo?: Video;
  // Shared link: this video alone.
  single?: boolean;
}

// Read only feed of the phone, one slide per video (react-swipeable-views). The next video starts
// when the finger lifts, during the slide. Only the current and next slides keep a video, the next
// one preloaded: the others free their memory.
const MobileFeed: React.FC<MobileFeedProps> = React.memo((props) => {
  const { user, firstVideo, single } = props;
  const { t } = useTranslation();
  const { redirectToApp } = useAuth() as Context;
  const [guestHomeVideos] = useManualQuery(GUEST_HOME_VIDEOS, { skipCache: true });
  const [videos, setVideos] = useState<Video[]>(firstVideo ? [firstVideo] : []);
  const [index, setIndex] = useState(0);
  // Video around which the players and blocks are kept. Follows the index once the slide is over:
  // mounting a video during it makes the slide stutter.
  const [windowIndex, setWindowIndex] = useState(0);
  // Paused by a tap: only the current video, a new one plays.
  const [paused, setPaused] = useState(false);
  // The browser is in the background (another app, another tab).
  const [hidden, setHidden] = useState(document.hidden);
  // Autoplay needs a muted video: the sound comes with the first tap.
  const [muted, setMuted] = useState(true);
  const [modalOpen, setModalOpen] = useState(true);
  const videoElsRef = useRef(new Map<number, HTMLVideoElement>());
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const videosRef = useRef(videos);
  videosRef.current = videos;
  const indexRef = useRef(index);
  indexRef.current = index;
  const loadingRef = useRef(false);
  const endedRef = useRef(Boolean(single));
  const introRef = useRef(true);
  const rootRef = useRef<HTMLDivElement>(null);

  const playing = !paused && !hidden;

  // The TikTok browser on iOS covers the bottom of the page: the footer and the column go up.
  // Its user agent says it ("musical_ly", "BytedanceWebview"); the referrer is often empty in it.
  const inTiktok = useMemo(() => (
    user.os === 'ios'
    && (/musical_ly|BytedanceWebview|TikTok/i.test(navigator.userAgent) || document.referrer.includes('tiktok'))
  ), [user.os]);

  const getVideos = useCallback(async () => {
    if (loadingRef.current || endedRef.current) {
      return;
    }
    loadingRef.current = true;
    let docs = [] as Video[];
    try {
      const { data } = await guestHomeVideos({ variables: { input: { limit: PAGE_LIMIT } } }) as any;
      docs = (data && data.guestVideosPagination && data.guestVideosPagination.docs) || [];
    } catch (e) {
      docs = [];
    }
    loadingRef.current = false;
    const ids = videosRef.current.map((v) => v.id);
    const fresh = docs.filter((v) => !ids.includes(v.id));
    // A page with only known videos: nothing more to show.
    if (docs.length && !fresh.length) {
      endedRef.current = true;
    }
    if (fresh.length) {
      setVideos((prev) => [...prev, ...fresh.filter((v) => !prev.some((p) => p.id === v.id))]);
    }
  }, [guestHomeVideos]);

  useEffect(() => {
    if (videos.length - index - 1 <= PRELOAD_THRESHOLD) {
      getVideos();
    }
  }, [getVideos, index, videos.length]);

  useEffect(() => {
    if (rootRef.current) {
      installDebug(rootRef.current);
    }
  }, []);

  useEffect(() => {
    const onVisibility = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Fired when the finger lifts on a swipe, before the slide: the video starts right away.
  const onChangeIndex = useCallback((next: number) => {
    log(`change ${indexRef.current} -> ${next}`);
    // Played here, during the touch: iOS only allows the sound to a video started by the user.
    const previousEl = videoElsRef.current.get(indexRef.current);
    const nextEl = videoElsRef.current.get(next);
    if (previousEl && previousEl !== nextEl) {
      previousEl.pause();
    }
    if (nextEl) {
      nextEl.muted = mutedRef.current;
      nextEl.play().catch(() => undefined);
    }
    setIndex(next);
    setPaused(false);
    // Two slides at once: the new video has no player yet.
    setWindowIndex((prev) => (Math.abs(next - prev) > 1 ? next : prev));
  }, []);

  const onTransitionEnd = useCallback(() => {
    setWindowIndex(indexRef.current);
  }, []);

  const onElement = useCallback((i: number, el: HTMLVideoElement | null) => {
    if (el) {
      videoElsRef.current.set(i, el);
    } else {
      videoElsRef.current.delete(i);
    }
  }, []);

  // Set on the element during the tap itself: iOS pauses a video unmuted later.
  const applyMuted = useCallback((value: boolean) => {
    const el = videoElsRef.current.get(indexRef.current);
    if (el) {
      el.muted = value;
    }
    setMuted(value);
  }, []);

  const onToggleMute = useCallback(() => {
    applyMuted(!muted);
  }, [applyMuted, muted]);

  const onForcedMute = useCallback(() => {
    setMuted(true);
  }, []);

  // The browser sends no click after a swipe: a tap here is a real tap.
  const onTap = useCallback((tapped: number) => {
    if (tapped === indexRef.current) {
      setPaused((prev) => !prev);
    }
  }, []);

  const openShortModal = useCallback(() => {
    setModalOpen(true);
  }, []);

  // Closing the first popup turns the sound on (its hint says so).
  const closeShortModal = useCallback(() => {
    if (introRef.current) {
      introRef.current = false;
      applyMuted(false);
    }
    setModalOpen(false);
  }, [applyMuted]);

  const current = videos[index] as Video | undefined;

  return (
    <Box ref={rootRef} sx={styles.feed}>
      <SwipeableViews
        axis="y"
        index={index}
        onChangeIndex={onChangeIndex}
        onTransitionEnd={onTransitionEnd}
        resistance
        ignoreNativeScroll
        containerStyle={styles.fullHeight}
        slideStyle={styles.slide}
        style={styles.fullHeight}
      >
        {videos.map((video, i) => (
          <MobileFeedItem
            key={video.id}
            index={i}
            video={video}
            near={Math.abs(i - windowIndex) <= 2}
            // The current video, and the next one preloaded. The one left keeps its player until the
            // slide is over: unmounting it during the slide makes it stutter.
            withPlayer={i === index || (EXP !== 'solo' && (i - windowIndex === 0 || i - windowIndex === 1))}
            current={i === index}
            playing={i === index && playing}
            muted={muted}
            paused={i === index && paused}
            inTiktok={inTiktok}
            onTap={onTap}
            openShortModal={openShortModal}
            onForcedMute={onForcedMute}
            onElement={onElement}
          />
        ))}
      </SwipeableViews>
      {current && (
        <IconButton onClick={onToggleMute} aria-label={muted ? t('admin.feed.sound') : t('admin.feed.mute')} sx={styles.mute}>
          <FontAwesomeIcon icon={muted ? faVolumeMute : faVolumeHigh} />
        </IconButton>
      )}
      {current && (
        <ShortModal
          codeshare={current.codeshare}
          isShortModalVisible={modalOpen}
          closeShortModal={closeShortModal}
          redirectToApp={redirectToApp}
          showSoundHint={muted}
        />
      )}
    </Box>
  );
});

export default MobileFeed;
